import { FC, useEffect, useState } from 'react';
import sectorsService from '@/services/sectors.service';
import { AgentScope, PublicSector } from '@/types/agents';
import { AdminAgentForm } from '@/types/adminAgent';

interface Props {
  form: AdminAgentForm;
  onChange: (patch: Partial<AdminAgentForm>) => void;
  /** Esconde a opção GLOBAL se o user não pode promover (curator). */
  allowGlobal?: boolean;
}

const SCOPE_HELP: Record<AgentScope, string> = {
  PERSONAL: 'Só você usa.',
  SECTOR: 'Visível para os setores selecionados.',
  GLOBAL: 'Visível para toda a organização.',
};

const DistributionTab: FC<Props> = ({ form, onChange, allowGlobal = true }) => {
  const [sectors, setSectors] = useState<PublicSector[]>([]);

  useEffect(() => {
    sectorsService.list().then(setSectors).catch(() => undefined);
  }, []);

  const toggleSector = (id: string) => {
    const next = form.sector_ids.includes(id)
      ? form.sector_ids.filter((s) => s !== id)
      : [...form.sector_ids, id];
    onChange({ sector_ids: next });
  };

  return (
    <div className="space-y-5" data-testid="tab-distribution">
      <fieldset>
        <legend className="text-sm font-semibold text-content-primary mb-2">Escopo</legend>
        <div className="space-y-2">
          {(['PERSONAL', 'SECTOR', 'GLOBAL'] as AgentScope[])
            .filter((s) => s !== 'GLOBAL' || allowGlobal)
            .map((scope) => (
              <label key={scope} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  value={scope}
                  data-testid={`scope-${scope.toLowerCase()}`}
                  checked={form.scope === scope}
                  onChange={() => onChange({ scope })}
                  className="accent-accent"
                />
                <span className="text-sm">
                  <strong className="text-content-primary">{scope}</strong>{' '}
                  <span className="text-content-tertiary">— {SCOPE_HELP[scope]}</span>
                </span>
              </label>
            ))}
        </div>
      </fieldset>

      {form.scope === 'SECTOR' && (
        <fieldset>
          <legend className="text-sm font-semibold text-content-primary mb-2">
            Setores ({form.sector_ids.length} selecionado{form.sector_ids.length === 1 ? '' : 's'})
          </legend>
          {sectors.length === 0 ? (
            <p className="text-sm text-content-tertiary">Carregando setores…</p>
          ) : (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-surface-border bg-surface-secondary p-2">
              {sectors.map((s) => (
                <label
                  key={s.id}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-surface-hover rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    data-testid={`sector-checkbox-${s.id}`}
                    checked={form.sector_ids.includes(s.id)}
                    onChange={() => toggleSector(s.id)}
                    className="accent-accent"
                  />
                  <span className="text-sm text-content-primary">{s.name}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
      )}

      <div className="flex flex-col gap-2 pt-2 border-t border-surface-border">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid="toggle-published"
            checked={form.is_published}
            onChange={(e) => onChange({ is_published: e.target.checked })}
            className="accent-accent"
          />
          <span className="text-sm text-content-primary">Publicado</span>
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid="toggle-active"
            checked={form.is_active}
            onChange={(e) => onChange({ is_active: e.target.checked })}
            className="accent-accent"
          />
          <span className="text-sm text-content-primary">Ativo (visível no Hub)</span>
        </label>
      </div>
    </div>
  );
};

export default DistributionTab;
